import { useEffect, useState } from "react";
import { getFirestore, collection, getDocs, addDoc, deleteDoc, doc, updateDoc } from "firebase/firestore";
import { useLocation } from "react-router-dom";
import app from "./firebase";
import styles from "./ItemsDashboard.module.css";
import { format2 } from "./utils/format2";

const db = getFirestore(app);

// Standard units for items
const STANDARD_UNITS = [
  "pieces", "units", "pcs", "sheets", "rolls", "boxes", "cartons", "cases", "bottles", "tubes", "bags", "pouches"
];

export default function ItemsDashboard() {
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ 
    name: "", 
    vendor: "", 
    currentStock: 0, 
    minStock: 5, 
    unit: "pieces",
    category: "packaging",
    costPerUnit: 0
  });
  const [vendorSearch, setVendorSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const location = useLocation();

  // Item categories
  const ITEM_CATEGORIES = [
    { value: "packaging", label: "Packaging" },
    { value: "labels", label: "Labels" },
    { value: "bottles", label: "Bottles" },
    { value: "pouches", label: "Pouches" },
    { value: "accessories", label: "Accessories" },
    { value: "supplies", label: "Supplies" },
    { value: "other", label: "Other" }
  ];

  // Fetch items and vendors
  const fetchData = async () => {
    setLoading(true);
    try {
      const [itemSnap, vendorSnap] = await Promise.all([
        getDocs(collection(db, "items")),
        getDocs(collection(db, "vendors")),
      ]);
      
      const vendorsArr = vendorSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setVendors(vendorsArr);

      const itemsArr = itemSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Add default values for stock management if they don't exist
        currentStock: doc.data().currentStock || 0,
        minStock: doc.data().minStock || 5,
        unit: doc.data().unit || "pieces",
        category: doc.data().category || "other",
        costPerUnit: doc.data().costPerUnit || 0,
        vendorName: vendorsArr.find(v => v.id === doc.data().vendorId)?.name || "Unknown Vendor"
      }));
      setItems(itemsArr);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Handle highlighting from search - triggers on any highlight event
  useEffect(() => {
    if (location.state?.highlightId && !loading && items.length > 0) {
      const scrollToElement = () => {
        const element = document.getElementById(`item-${location.state.highlightId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.classList.add(styles.highlighted);
          setTimeout(() => {
            element.classList.remove(styles.highlighted);
          }, 3000);
          return true;
        }
        return false;
      };

      // Try immediately first
      if (!scrollToElement()) {
        // If element not found, wait a bit for DOM to update
        const timer = setTimeout(() => {
          scrollToElement();
        }, 100);
        return () => clearTimeout(timer);
      }
    }
  }, [location.state, loading, items]);

  // Filter vendors by search
  const filteredVendors = vendors.filter(vendor =>
    vendor.name.toLowerCase().includes(vendorSearch.toLowerCase())
  );

  // Filter items by search term and category
  const filteredItems = items.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.vendorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === "all" || item.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  // Get low stock and out of stock items
  const getLowStockItems = () => {
    return items.filter(item => 
      item.currentStock <= item.minStock && item.currentStock > 0
    );
  };

  const getOutOfStockItems = () => {
    return items.filter(item => 
      item.currentStock === 0
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.vendor) return;

    // Check if vendor exists
    let vendorId = "";
    const existing = vendors.find(v => v.name.toLowerCase() === form.vendor.toLowerCase());
    if (existing) {
      vendorId = existing.id;
    } else {
      // Create new vendor
      const docRef = await addDoc(collection(db, "vendors"), { 
        name: form.vendor,
        contact: "",
        email: "",
        phone: ""
      });
      vendorId = docRef.id;
      setVendors([...vendors, { id: vendorId, name: form.vendor, contact: "", email: "", phone: "" }]);
    }

    await addDoc(collection(db, "items"), {
      name: form.name,
      vendorId,
      currentStock: parseInt(form.currentStock) || 0,
      minStock: parseInt(form.minStock) || 5,
      unit: form.unit || "pieces",
      category: form.category || "other",
      costPerUnit: parseFloat(form.costPerUnit) || 0,
      createdAt: new Date(),
      lastUpdated: new Date(),
    });
    setForm({ name: "", vendor: "", currentStock: 0, minStock: 5, unit: "pieces", category: "packaging", costPerUnit: 0 });
    setVendorSearch("");
    setShowAdd(false);
    fetchData();
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm("Delete this item?")) return;
    await deleteDoc(doc(db, "items", itemId));
    fetchData();
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setForm({ 
      name: item.name, 
      vendor: item.vendorName,
      currentStock: item.currentStock || 0,
      minStock: item.minStock || 5,
      unit: item.unit || "pieces",
      category: item.category || "other",
      costPerUnit: item.costPerUnit || 0
    });
    setVendorSearch(item.vendorName);
    setShowEdit(true);
  };

  const handleClone = (item) => {
    setEditItem(null); // Set to null for add mode
    setForm({ 
      name: `${item.name} (Copy)`, 
      vendor: item.vendorName,
      currentStock: 0, // Reset stock for cloned item
      minStock: item.minStock || 5,
      unit: item.unit || "pieces",
      category: item.category || "other",
      costPerUnit: item.costPerUnit || 0
    });
    setVendorSearch(item.vendorName);
    setShowAdd(true); // Use add modal for cloning
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.vendor) return;

    // Check if vendor exists or create
    let vendorId = "";
    const existing = vendors.find(v => v.name.toLowerCase() === form.vendor.toLowerCase());
    if (existing) {
      vendorId = existing.id;
    } else {
      const docRef = await addDoc(collection(db, "vendors"), { 
        name: form.vendor,
        contact: "",
        email: "",
        phone: ""
      });
      vendorId = docRef.id;
      setVendors([...vendors, { id: vendorId, name: form.vendor, contact: "", email: "", phone: "" }]);
    }

    await updateDoc(doc(db, "items", editItem.id), {
      name: form.name,
      vendorId,
      currentStock: parseInt(form.currentStock) || 0,
      minStock: parseInt(form.minStock) || 5,
      unit: form.unit || "pieces",
      category: form.category || "other",
      costPerUnit: parseFloat(form.costPerUnit) || 0,
      lastUpdated: new Date(),
    });
    setForm({ name: "", vendor: "", currentStock: 0, minStock: 5, unit: "pieces", category: "packaging", costPerUnit: 0 });
    setVendorSearch("");
    setShowEdit(false);
    setEditItem(null);
    fetchData();
  };

  const getStockStatus = (item) => {
    if (item.currentStock === 0) return "out-of-stock";
    if (item.currentStock <= item.minStock) return "low-stock";
    return "in-stock";
  };

  const getStockStatusText = (item) => {
    if (item.currentStock === 0) return "Out of Stock";
    if (item.currentStock <= item.minStock) return "Low Stock";
    return "In Stock";
  };

  const getCategoryIcon = (category) => {
    const icons = {
      packaging: "📦",
      labels: "🏷️",
      bottles: "🍶",
      pouches: "👝",
      accessories: "🔧",
      supplies: "📎",
      other: "📋"
    };
    return icons[category] || "📋";
  };

  const lowStockCount = getLowStockItems().length;
  const outOfStockCount = getOutOfStockItems().length;

  return (
    <div className={styles.dashboardContainer}>
      <div className={styles.header}>
        <h2 className={styles.dashboardTitle}>Items Inventory</h2>
        <button
          className={styles.addButton}
          onClick={() => setShowAdd(true)}
        >
          + Add Item
        </button>
      </div>

      {/* Quick Stats */}
      <div className={styles.statsContainer}>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>{format2(items.length)}</div>
          <div className={styles.statLabel}>Total Items</div>
        </div>
        <div className={`${styles.statCard} ${outOfStockCount > 0 ? styles.criticalStat : ''}`}>
          <div className={styles.statNumber}>{format2(outOfStockCount)}</div>
          <div className={styles.statLabel}>Out of Stock</div>
        </div>
        <div className={`${styles.statCard} ${lowStockCount > 0 ? styles.warningStat : ''}`}>
          <div className={styles.statNumber}>{format2(lowStockCount)}</div>
          <div className={styles.statLabel}>Low Stock</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>{format2(vendors.length)}</div>
          <div className={styles.statLabel}>Vendors</div>
        </div>
      </div>

      {/* Filters */}
      <div className={styles.filtersContainer}>
        <div className={styles.searchContainer}>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search items by name, vendor, or category..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select
          className={styles.filterSelect}
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
        >
          <option value="all">All Categories</option>
          {ITEM_CATEGORIES.map(cat => (
            <option key={cat.value} value={cat.value}>{cat.label}</option>
          ))}
        </select>
      </div>

      {/* Items Grid */}
      <div className={styles.itemsGrid}>
        {filteredItems.map(item => (
          <div 
            key={item.id} 
            id={`item-${item.id}`}
            className={`${styles.itemCard} ${styles[getStockStatus(item)]}`}
          >
            <div className={styles.itemHeader}>
              <div className={styles.itemIcon}>{getCategoryIcon(item.category)}</div>
              <div className={styles.itemName}>{item.name}</div>
            </div>
            
            <div className={styles.itemDetails}>
              <div className={styles.itemCategory}>
                {ITEM_CATEGORIES.find(c => c.value === item.category)?.label || "Other"}
              </div>
              <div className={styles.itemVendor}>Vendor: {item.vendorName}</div>
              <div className={styles.stockInfo}>
                <span className={styles.currentStock}>
                  {format2(item.currentStock)} {item.unit}
                </span>
                <span className={styles.minStock}>
                  Min: {format2(item.minStock)} {item.unit}
                </span>
              </div>
              {item.costPerUnit > 0 && (
                <div className={styles.costInfo}>
                  Cost: ${item.costPerUnit.toFixed(2)} per {item.unit}
                </div>
              )}
              <div className={`${styles.stockStatus} ${styles[getStockStatus(item)]}`}>
                {getStockStatusText(item)}
              </div>
            </div>

            <div className={styles.itemActions}>
              <button
                className={styles.editButton}
                onClick={() => handleEdit(item)}
              >
                Edit
              </button>
              <button
                className={styles.cloneButton}
                onClick={() => handleClone(item)}
              >
                Clone
              </button>
              <button
                className={styles.deleteButton}
                onClick={() => handleDelete(item.id)}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredItems.length === 0 && (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>📦</div>
          <div className={styles.emptyText}>No items found</div>
          <div className={styles.emptySubtext}>
            {searchTerm || filterCategory !== "all" 
              ? "Try adjusting your search or filters" 
              : "Add your first item to get started"}
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {(showAdd || showEdit) && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h3>{showEdit ? "Edit Item" : "Add Item"}</h3>
            <form onSubmit={showEdit ? handleEditSubmit : handleSubmit}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Item Name</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g., Glass Bottles 500ml"
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Category</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                  >
                    {ITEM_CATEGORIES.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>Vendor</label>
                <input
                  type="text"
                  value={form.vendor}
                  onChange={(e) => {
                    setForm({ ...form, vendor: e.target.value });
                    setVendorSearch(e.target.value);
                  }}
                  placeholder="Search or type to add new vendor"
                  list="vendor-options"
                  required
                />
                <datalist id="vendor-options">
                  {filteredVendors.map(vendor => (
                    <option key={vendor.id} value={vendor.name} />
                  ))}
                </datalist>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Current Stock</label>
                  <input
                    type="number"
                    min="0"
                    value={form.currentStock}
                    onChange={(e) => setForm({ ...form, currentStock: e.target.value })}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Minimum Stock</label>
                  <input
                    type="number"
                    min="0"
                    value={form.minStock}
                    onChange={(e) => setForm({ ...form, minStock: e.target.value })}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Unit</label>
                  <select
                    value={form.unit}
                    onChange={(e) => setForm({ ...form, unit: e.target.value })}
                  >
                    {STANDARD_UNITS.map(unit => (
                      <option key={unit} value={unit}>{unit}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>Cost Per Unit ($)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.costPerUnit}
                  onChange={(e) => setForm({ ...form, costPerUnit: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              
              <div className={styles.modalActions}>
                <button
                  type="button"
                  onClick={() => {
                    setShowAdd(false);
                    setShowEdit(false);
                    setForm({ name: "", vendor: "", currentStock: 0, minStock: 5, unit: "pieces", category: "packaging", costPerUnit: 0 });
                    setVendorSearch("");
                    setEditItem(null);
                  }}
                  className={styles.cancelButton}
                >
                  Cancel
                </button>
                <button type="submit" className={styles.saveButton}>
                  {showEdit ? "Update" : "Save"}
                </button>
              </div>
              {form.vendor && !filteredVendors.some(v => v.name.toLowerCase() === form.vendor.toLowerCase()) && (
                <div className={styles.newVendorNotice}>
                  This will create a new vendor: <b>{form.vendor}</b>
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
